import React from 'react';
import { useTranslation } from 'react-i18next';
import { KoobInputSelect } from '@koob/ui';
import { AdminBookingStateEnum } from '@koob/enums';
import { FOLDER_ROWS_ALL, FolderTripStatusEnum } from '@koob/utils';

const TRIP_STATUSES = [FolderTripStatusEnum.DRAFT, FolderTripStatusEnum.BOOKED];

// pending_source is filed under pending (getFolderServiceFilterStatus);
// pre_confirmed stands on its own.
const SERVICE_STATUSES = [
  AdminBookingStateEnum.SENT,
  AdminBookingStateEnum.PENDING,
  AdminBookingStateEnum.PRE_CONFIRMED,
  AdminBookingStateEnum.CONFIRMED,
  AdminBookingStateEnum.REFUSED,
  AdminBookingStateEnum.CANCELLED,
];

// Labels follow BookingStatusBadge, whose key for "canceled" is "cancelled".
const statusLabelKey = (status: string) =>
  status === AdminBookingStateEnum.CANCELLED ? 'cancelled' : status;

const GROUP_PREFIX = '__group__';

export interface FolderBookingStatusFilterProps {
  status: string;
  onStatusChange: (status: string) => void;
  /** Trip statuses offered; none drops the "Trips" group and its heading. */
  tripStatuses?: string[];
  /** Hotel and experience statuses offered. */
  serviceStatuses?: string[];
}

/**
 * KD-3842 status filter of a client folder bookings list: trip statuses,
 * then hotel and experience statuses, each under its heading.
 */
export default function FolderBookingStatusFilter({
  status,
  onStatusChange,
  tripStatuses = TRIP_STATUSES,
  serviceStatuses = SERVICE_STATUSES,
}: FolderBookingStatusFilterProps) {
  const { t } = useTranslation('folders');

  // Under group headings, the statuses sit indented below their group.
  const grouped = tripStatuses.length > 0;
  const options = [
    { value: FOLDER_ROWS_ALL, label: t('bookingsList.statusAll') },
    ...(grouped
      ? [
          {
            value: `${GROUP_PREFIX}trips`,
            label: t('bookingsList.statusGroupTrips'),
            disabled: true,
          },
          ...tripStatuses.map((value) => ({
            value,
            label: t(`bookings.status.${value}`),
            item: { indent: true },
          })),
          {
            value: `${GROUP_PREFIX}services`,
            label: t('bookingsList.statusGroupServices'),
            disabled: true,
          },
        ]
      : []),
    ...serviceStatuses.map((value) => ({
      value,
      label: t(`bookings.status.${statusLabelKey(value)}`),
      item: { indent: grouped },
    })),
  ];

  return (
    <KoobInputSelect
      options={options}
      value={status}
      onValueChange={(value: string) =>
        onStatusChange(value ?? FOLDER_ROWS_ALL)
      }
      renderOption={(option) => {
        if (String(option.value).startsWith(GROUP_PREFIX)) {
          return (
            <span className="font-bold text-gray-900">{option.label}</span>
          );
        }
        return option.item?.indent ? (
          <span className="pl-3">{option.label}</span>
        ) : (
          option.label
        );
      }}
      data-testid="folder-bookings-status-filter"
    />
  );
}
